import React from 'react';
import {FormattedMessage} from 'react-intl';
import keyMirror from 'keymirror';

import successImage from '../assets/icon--success.svg';

const AlertTypes = keyMirror({
    STANDARD: null,
    EXTENSION: null,
    INLINE: null
});

const AlertLevels = {
    SUCCESS: 'success',
    INFO: 'info',
    WARN: 'warn'
};

const alerts = [
    {
        alertId: 'createSuccess',
        alertType: AlertTypes.STANDARD,
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        content: (
            <FormattedMessage
                defaultMessage="New project created."
                description="Message indicating that project was successfully created"
                id="gui.alerts.createsuccess"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        alertId: 'createCopySuccess',
        alertType: AlertTypes.STANDARD,
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        content: (
            <FormattedMessage
                defaultMessage="Project saved as a copy."
                description="Message indicating that project was successfully created"
                id="gui.alerts.createcopysuccess"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        alertId: 'createRemixSuccess',
        alertType: AlertTypes.STANDARD,
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        content: (
            <FormattedMessage
                defaultMessage="Project saved as a remix."
                description="Message indicating that project was successfully created"
                id="gui.alerts.createremixsuccess"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        alertId: 'creating',
        alertType: AlertTypes.STANDARD,
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        content: (
            <FormattedMessage
                defaultMessage="Creating new…"
                description="Message indicating that project is in process of creating"
                id="gui.alerts.creating"
            />
        ),
        iconSpinner: true,
        level: AlertLevels.SUCCESS
    },
    {
        alertId: 'creatingCopy',
        alertType: AlertTypes.STANDARD,
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        content: (
            <FormattedMessage
                defaultMessage="Copying project…"
                description="Message indicating that project is in process of copying"
                id="gui.alerts.creatingCopy"
            />
        ),
        iconSpinner: true,
        level: AlertLevels.SUCCESS
    },
    {
        alertId: 'creatingRemix',
        alertType: AlertTypes.STANDARD,
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        content: (
            <FormattedMessage
                defaultMessage="Remixing project…"
                description="Message indicating that project is in process of remixing"
                id="gui.alerts.creatingRemix"
            />
        ),
        iconSpinner: true,
        level: AlertLevels.SUCCESS
    },
    {
        alertId: 'creatingError',
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Could not create the project. Please try again!"
                description="Message indicating that project could not be created"
                id="gui.alerts.creatingError"
            />
        ),
        level: AlertLevels.WARN
    },
    {
        alertId: 'savingError',
        clearList: ['createSuccess', 'creating', 'createCopySuccess', 'creatingCopy',
            'createRemixSuccess', 'creatingRemix', 'saveSuccess', 'saving'],
        showDownload: true,
        showSaveNow: true,
        closeButton: false,
        content: (
            <FormattedMessage
                defaultMessage="Project could not save."
                description="Message indicating that project could not be saved"
                id="gui.alerts.savingError"
            />
        ),
        level: AlertLevels.WARN
    },
    {
        alertId: 'saveSuccess',
        alertType: AlertTypes.INLINE,
        clearList: ['saveSuccess', 'saving', 'savingError'],
        content: (
            <FormattedMessage
                defaultMessage="Project saved."
                description="Message indicating that project was successfully saved"
                id="gui.alerts.savesuccess"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    {
        alertId: 'saving',
        alertType: AlertTypes.INLINE,
        clearList: ['saveSuccess', 'saving', 'savingError'],
        content: (
            <FormattedMessage
                defaultMessage="Saving project…"
                description="Message indicating that project is in process of saving"
                id="gui.alerts.saving"
            />
        ),
        iconSpinner: true,
        level: AlertLevels.INFO
    },
    {
        alertId: 'cloudInfo',
        alertType: AlertTypes.STANDARD,
        clearList: ['cloudInfo'],
        content: (
            <FormattedMessage
                defaultMessage="Please note, cloud variables only support numbers, not letters or symbols. {learnMoreLink}" // eslint-disable-line max-len
                description="Info about cloud variable limitations"
                id="gui.alerts.cloudInfo"
                values={{
                    learnMoreLink: (
                        <a
                            href="https://scratch.mit.edu/info/faq/#clouddata"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <FormattedMessage
                                defaultMessage="Learn more."
                                description="Link text to cloud var faq"
                                id="gui.alerts.cloudInfoLearnMore"
                            />
                        </a>
                    )
                }}
            />
        ),
        closeButton: true,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 15
    },
    {
        alertId: 'importingAsset',
        alertType: AlertTypes.STANDARD,
        clearList: [],
        content: (
            <FormattedMessage
                defaultMessage="Importing…"
                description="Message indicating that project is in process of importing"
                id="gui.alerts.importing"
            />
        ),
        iconSpinner: true,
        level: AlertLevels.SUCCESS
    },
    {
        alertId: 'workspaceIsEmpty',
        alertType: AlertTypes.STANDARD,
        clearList: ['workspaceIsEmpty'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Workspace is empty"
                description="Message indicating that workspace is empty"
                id="gui.alerts.workspaceIsEmpty"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    {
        alertId: 'workspaceIsNotEmpty',
        alertType: AlertTypes.STANDARD,
        clearList: ['workspaceIsNotEmpty'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Workspace is not empty, plese clean you workspace first"
                description="Message indicating that workspace is not empty"
                id="gui.alerts.workspaceIsNotEmpty"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    {
        alertId: 'uploadError',
        alertType: AlertTypes.STANDARD,
        clearList: ['uploadError'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Upload error"
                description="Message indicating that upload progress is error"
                id="gui.alerts.uploadError"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    {
        alertId: 'uploadSuccess',
        alertType: AlertTypes.STANDARD,
        clearList: ['uploadSuccess', 'uploadError'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Upload success"
                description="Message indicating that upload progress is success"
                id="gui.alerts.uploadSuccess"
            />
        ),
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        alertId: 'selectADeviceFirst',
        alertType: AlertTypes.STANDARD,
        clearList: ['selectADeviceFirst'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Plese select a device first"
                description="Message indicating that device is not selected"
                id="gui.alerts.selectADeviceFirst"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    {
        alertId: 'connectAPeripheralFirst',
        alertType: AlertTypes.STANDARD,
        clearList: ['connectAPeripheralFirst'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Plese connect a peripheral first"
                description="Message indicating that no peripheral is connected"
                id="gui.alerts.connectAPeripheralFirst"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    {
        alertId: 'codeEditorIsLocked',
        alertType: AlertTypes.STANDARD,
        clearList: ['codeEditorIsLocked', 'codeEditorIsUnlocked'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Code editor is in lock state, if you want to edit the code, please click the unlock button first" // eslint-disable-line max-len
                description="Message indicating that code editor is in lock state"
                id="gui.alerts.codeEditorIsLocked"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    {
        alertId: 'codeEditorIsUnlocked',
        alertType: AlertTypes.STANDARD,
        clearList: ['codeEditorIsUnlocked', 'codeEditorIslocked'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Code editor is in unlock state, if you want to generate code from block, please click the lock button first" // eslint-disable-line max-len
                description="Message indicating that code editor is in unlock state"
                id="gui.alerts.codeEditorIsUnlocked"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-01 — Project created (NewProjectModal success toast).
    // Added alphabetically (projectCreated < projectCreateError) so subsequent
    // Phase 3 plans 03-03/04/05/06 can append their alerts in order.
    {
        alertId: 'projectCreated',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectCreated', 'projectCreateError'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Dự án đã được tạo thành công"
                description="Toast: project successfully created (PRJ-01)"
                id="gen.alert.projectCreated"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 3 PRJ-01 — Project create error (VM load rejection).
    {
        alertId: 'projectCreateError',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectCreated', 'projectCreateError'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Không thể tạo dự án"
                description="Toast: project creation failed (PRJ-01)"
                id="gen.alert.projectCreateError"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-02 — Project loaded successfully (real vmState
    // path — user opened a previously-saved entry). Added alphabetically
    // (projectLoadError < projectLoaded < projectLoadedMock) so subsequent
    // Phase 3 plans 03-04/05/06 can append their alerts without merge
    // conflicts. clearList shares all 3 PRJ-02 ids so opening a new modal
    // after a stale toast clears the previous one.
    {
        alertId: 'projectLoaded',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectLoaded', 'projectLoadedMock', 'projectLoadError'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đã tải dự án"
                description="Toast: project loaded successfully (real vmState — PRJ-02)"
                id="gen.alert.projectLoaded"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 3 PRJ-02 — Mock project fake-loaded (DP-05). VN copy
    // appends "(dự án mẫu)" suffix so SR/sighted users see the distinction.
    {
        alertId: 'projectLoadedMock',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectLoaded', 'projectLoadedMock', 'projectLoadError'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đã tải dự án (dự án mẫu)"
                description="Toast: mock project fake-loaded (DP-05, PRJ-02)"
                id="gen.alert.projectLoadedMock"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 3 PRJ-02 — Load failure (vm.loadProject rejected).
    {
        alertId: 'projectLoadError',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectLoaded', 'projectLoadedMock', 'projectLoadError'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Không thể tải dự án"
                description="Toast: project load failed (PRJ-02)"
                id="gen.alert.projectLoadError"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-03 — Project saved successfully (quick-save
    // Ctrl+S or first-save modal). Added alphabetically (projectSaveError <
    // projectSaved < projectTooLarge) so subsequent Phase 3 plans 03-05/06
    // can append their alerts without merge conflicts. clearList shares all
    // 4 PRJ-03 ids so opening a new modal after a stale toast clears it.
    {
        alertId: 'projectSaved',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectSaved', 'projectSaveError', 'projectTooLarge', 'localStorageFull'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Dự án đã được lưu thành công"
                description="Toast: project save succeeded (PRJ-03)"
                id="gen.alert.projectSaved"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 3 PRJ-03 — Generic save error (unexpected exception
    // during vm.toJSON or persist call).
    {
        alertId: 'projectSaveError',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectSaved', 'projectSaveError', 'projectTooLarge', 'localStorageFull'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Không thể lưu dự án"
                description="Toast: project save failed (PRJ-03)"
                id="gen.alert.projectSaveError"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-03 — Project size exceeded (vmState > 500KB).
    // Mitigation for T-03-04-01: oversize JSON DoS via complex projects.
    {
        alertId: 'projectTooLarge',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectSaved', 'projectSaveError', 'projectTooLarge', 'localStorageFull'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Dự án quá lớn để lưu trữ tạm. Vui lòng đơn giản hóa hoặc lưu .sb3 về máy tính"
                description="Toast: vmState size exceeds 500KB cap (PRJ-03)"
                id="gen.alert.projectTooLarge"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-03 — localStorage QuotaExceededError surfaced
    // via writeMyProjects onQuotaError callback. Mitigation for T-03-04-02.
    {
        alertId: 'localStorageFull',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectSaved', 'projectSaveError', 'projectTooLarge', 'localStorageFull'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Bộ nhớ trình duyệt đã đầy. Vui lòng xoá bớt dự án cũ"
                description="Toast: localStorage quota exceeded (PRJ-03)"
                id="gen.alert.localStorageFull"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-04 — Share success toast (ShareProjectModal
    // step 1 → step 2 transition). Added alphabetically after the PRJ-03
    // group (linkCopied < linkCopyFailed < projectShared < projectShareError
    // grouped per-feature for clarity). clearList shares all 4 PRJ-04 ids
    // so opening the modal again after a stale toast clears the previous.
    {
        alertId: 'projectShared',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectShared', 'projectShareError', 'linkCopied', 'linkCopyFailed'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Dự án đã được chia sẻ thành công"
                description="Toast: project share succeeded (PRJ-04)"
                id="gen.alert.projectShared"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 3 PRJ-04 — Share error toast (reserved for future
    // BE integration failure paths — not dispatched by current mock flow
    // since buildShareLink is synchronous and cannot fail).
    {
        alertId: 'projectShareError',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectShared', 'projectShareError', 'linkCopied', 'linkCopyFailed'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Không thể chia sẻ dự án, vui lòng thử lại"
                description="Toast: project share failed (PRJ-04)"
                id="gen.alert.projectShareError"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-04 — Clipboard copy succeeded (Step 2 "Sao
    // chép link" button click resolves).
    {
        alertId: 'linkCopied',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectShared', 'projectShareError', 'linkCopied', 'linkCopyFailed'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đã sao chép link chia sẻ"
                description="Toast: share link copied to clipboard (PRJ-04 DP-06)"
                id="gen.alert.linkCopied"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 3 PRJ-04 — Clipboard copy failed (e.g., navigator.
    // clipboard blocked + execCommand fallback also rejected). Surfaced as
    // WARN so user can copy manually from the read-only input.
    {
        alertId: 'linkCopyFailed',
        alertType: AlertTypes.STANDARD,
        clearList: ['projectShared', 'projectShareError', 'linkCopied', 'linkCopyFailed'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Không thể sao chép. Vui lòng sao chép thủ công"
                description="Toast: clipboard copy failed (PRJ-04)"
                id="gen.alert.linkCopyFailed"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 3 PRJ-05 — Shared project viewed (read-only fake-load
    // via SharedProjectsModal Xem button). Grouped with sharedCopied so
    // re-opening the modal clears stale toasts.
    {
        alertId: 'sharedViewed',
        alertType: AlertTypes.STANDARD,
        clearList: ['sharedViewed', 'sharedCopied'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đã tải dự án (chế độ chỉ xem)"
                description="Toast: shared project loaded read-only (PRJ-05)"
                id="gen.alert.sharedViewed"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 3 PRJ-05 — Shared project copied to my-projects via
    // SharedProjectsModal "Lưu copy" button. Only fires when permission='copy'.
    {
        alertId: 'sharedCopied',
        alertType: AlertTypes.STANDARD,
        clearList: ['sharedViewed', 'sharedCopied'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đã lưu bản sao vào không gian cá nhân"
                description="Toast: shared project saved as copy to my-projects (PRJ-05)"
                id="gen.alert.sharedCopied"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 4 ROB-02 — Composite "Robot dò đường" run toast (mock).
    // Dispatched GUI-side from vm-listener-hoc when the lineFollow composite
    // opcode runs (the VM extension emits MBF_ROBOT_COMPOSITE_RUN on the
    // runtime EventEmitter; the HOC maps the composite key → this alert id).
    // The 3 composite toasts share a clearList so a rapid re-run replaces the
    // previous one rather than stacking. MOCK-ONLY (DR-00) — no hardware.
    {
        alertId: 'robotLineFollow',
        alertType: AlertTypes.STANDARD,
        clearList: ['robotLineFollow', 'robotAvoidObstacle', 'robotFollowObject'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đang dò đường… (mô phỏng)"
                description="Toast: line-follow composite running (mock — ROB-02)"
                id="gen.robot.toast.lineFollow"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 4 ROB-03 — Composite "Robot tránh vật cản" run toast (mock).
    {
        alertId: 'robotAvoidObstacle',
        alertType: AlertTypes.STANDARD,
        clearList: ['robotLineFollow', 'robotAvoidObstacle', 'robotFollowObject'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đang tránh vật cản… (mô phỏng)"
                description="Toast: avoid-obstacle composite running (mock — ROB-03)"
                id="gen.robot.toast.avoidObstacle"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 4 ROB-04 — Composite "Robot bám theo vật" run toast (mock).
    {
        alertId: 'robotFollowObject',
        alertType: AlertTypes.STANDARD,
        clearList: ['robotLineFollow', 'robotAvoidObstacle', 'robotFollowObject'],
        closeButton: true,
        content: (
            <FormattedMessage
                defaultMessage="Đang bám theo vật… (mô phỏng)"
                description="Toast: follow-object composite running (mock — ROB-04)"
                id="gen.robot.toast.followObject"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 3
    },
    // MSTEM-ADDED Phase 6 / Plan 06-01 — "Khối Mở rộng" authoring toasts
    // (EXT-01..04, MOCK-ONLY). Each is a success/error toast that auto-dismisses
    // via showAlertWithTimeout (maxDisplaySecs). Feature plans 06-02..06-05
    // dispatch these by alertId; the shared alerts file is touched ONLY here.
    {
        // EXT-01 — device declaration saved to mstem.customDevices.
        alertId: 'extDeviceSaved',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã khai báo thiết bị. Thiết bị xuất hiện trong thư viện.'}
                description="Toast: custom device saved + appears in device library (EXT-01)"
                id="gen.ext.device.saved"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        // EXT-01 — save failed (localStorage quota / oversized icon).
        alertId: 'extDeviceSaveError',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Không thể lưu thiết bị. Bộ nhớ trình duyệt có thể đã đầy.'}
                description="Toast: custom device save failed (EXT-01)"
                id="gen.ext.device.saveError"
            />
        ),
        level: AlertLevels.WARN,
        maxDisplaySecs: 5
    },
    {
        // EXT-02 — custom block saved to mstem.customBlocks.
        alertId: 'extBlockSaved',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã thêm khối vào bảng khối “🧩 Khối của tôi”. Kéo khối từ palette vào vùng lập trình.'}
                description="Toast: custom block saved (EXT-02)"
                id="gen.ext.block.saved"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        // EXT-02 — "Test trong workspace" preview added (mock, no Blockly inject).
        alertId: 'extBlockTestAdded',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã thêm khối thử vào khu xem trước. (Mô phỏng — khối thật do hệ thống xử lý sau.)'}
                description="Toast: test block added to the preview sandbox (mock — EXT-02)"
                id="gen.ext.block.testToast"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        // EXT-03 — LED 8×8 pattern copied to clipboard.
        alertId: 'extLedCopied',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã sao chép mẫu LED.'}
                description="Toast: LED 8x8 pattern copied to clipboard (EXT-03)"
                id="gen.ext.led.copied"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        // EXT-04 — mock publish registry URL copied to clipboard.
        alertId: 'extPublishLinkCopied',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã sao chép đường dẫn công bố.'}
                description="Toast: mock publish registry URL copied (EXT-04)"
                id="gen.ext.publish.linkCopied"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    // UC-43 / UC-47 management toasts (MOCK-ONLY) — fired by the two
    // "Quản lý ... mở rộng" managers after an edit (update) or delete.
    {
        // UC-43 S-2 — device updated in mstem.customDevices.
        alertId: 'extDeviceUpdated',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã cập nhật thiết bị.'}
                description="Toast: custom device updated (UC-43)"
                id="gen.ext.manage.deviceUpdated"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        // UC-43 S-3 — device removed from mstem.customDevices.
        alertId: 'extDeviceDeleted',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã xóa thiết bị.'}
                description="Toast: custom device deleted (UC-43)"
                id="gen.ext.manage.deviceDeleted"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        // UC-47 S-2 — block updated in mstem.customBlocks.
        alertId: 'extBlockUpdated',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã cập nhật khối.'}
                description="Toast: custom block updated (UC-47)"
                id="gen.ext.manage.blockUpdated"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    {
        // UC-47 S-3 — block removed from mstem.customBlocks.
        alertId: 'extBlockDeleted',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã xóa khối.'}
                description="Toast: custom block deleted (UC-47)"
                id="gen.ext.manage.blockDeleted"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    },
    // MSTEM-ADDED Phase 7 / Plan 07-03 — AI panel control-click result toast
    // (AI-01, MOCK-ONLY). Fired after the "Đang phân tích…" delay when a panel
    // control card button resolves its mock result from ai-results.js.
    {
        alertId: 'mstemAiResult',
        alertType: AlertTypes.STANDARD,
        content: (
            <FormattedMessage
                defaultMessage={'Đã phân tích (mô phỏng).'}
                description="Toast: AI panel control click resolved a mock result (AI-01)"
                id="gen.ai.panel.resultToast"
            />
        ),
        iconURL: successImage,
        level: AlertLevels.SUCCESS,
        maxDisplaySecs: 5
    }
];

export {
    alerts as default,
    AlertLevels,
    AlertTypes
};
